import React from "react";
import { type RegisteredStatsigEvent, useStatsigClient } from "@repo/statsig";
import classNames from "classnames";

export interface SummaryCardProps {
  totalPrice: number | null;
  monthlyPrice: number | null;
  taxes: number;
  isMonthly: boolean;
  setIsMonthly: (isMonthly: boolean) => void;
  clientTypography: Record<
    string,
    React.FC<{ children: React.ReactNode; className?: string }>
  >;
  theme: SummaryCardTheme;
  trialDays: number;
  productName: string;
}

export type SummaryCardTheme = {
  activeButtonColor: string;
  backgroundColor: string;
  dividerColor: string;
  /** Optional override for the "ORDER SUMMARY" label color.
   *  Defaults to `text-black-100` (Kinsa's hardcoded value) when omitted. */
  labelColor?: string;
  /** Optional override for the "{N}-Day Free Trial" sub-line color.
   *  Defaults to `text-black-200` (Kinsa's hardcoded value) when omitted. */
  trialColor?: string;
  /** Container padding override (default `24px 16px`). */
  padding?: string;
  /** Container border-radius override (default `12px`). */
  borderRadius?: string;
  /** Override for the section label text (default `"ORDER SUMMARY"`). */
  labelText?: string;
  /** Extra classes for the section label (controls size/case). */
  labelClassName?: string;
  /** Render the trial line in `BodyText` instead of `BodyTitleText`. */
  trialUseBodyStyle?: boolean;
  /** Override the trial line copy. Defaults to `"{N}-Day Free Trial"`. */
  trialCopyTemplate?: (days: number) => string;
  /** Optional override for the price-body line color. */
  priceBodyClassName?: string;
  /** Render the "Due Today / $0" footer in `BodyText` instead of `HeadlineBodyText`. */
  footerUseBodyStyle?: boolean;
  /** Extra classes for the footer label/value (e.g. `font-bold text-on-surface`). */
  footerClassName?: string;
};

const PriceHeadline: React.FC<{
  price: number | null;
  monthly: boolean;
  startDate: string;
  trialing: boolean;
  trialDays: number;
  BodyText: React.FC<{ children: React.ReactNode; className?: string }>;
  HeadlineBodyText: React.FC<{ children: React.ReactNode; className?: string }>;
  BodyTitleText: React.FC<{ children: React.ReactNode; className?: string }>;
  productName: string;
  trialColor?: string;
  trialUseBodyStyle?: boolean;
  trialCopyTemplate?: (days: number) => string;
  priceBodyClassName?: string;
}> = ({
  price,
  monthly,
  startDate,
  BodyText,
  HeadlineBodyText,
  BodyTitleText,
  trialing,
  trialDays,
  productName,
  trialColor,
  trialUseBodyStyle,
  trialCopyTemplate,
  priceBodyClassName,
}) => {
  const TrialText = trialUseBodyStyle ? BodyText : BodyTitleText;
  const trialCopy = trialCopyTemplate
    ? trialCopyTemplate(trialDays)
    : `${trialDays}-Day Free Trial`;
  return (
    <div>
      <HeadlineBodyText>{productName}</HeadlineBodyText>
      {trialing && (
        <TrialText className={trialColor ?? "text-black-200"}>{trialCopy}</TrialText>
      )}
      <BodyText className={priceBodyClassName}>
        {price != null ? (
          `$${price.toFixed(0)}/month billed ${monthly ? "monthly" : "annually"} ${trialing ? `on ${startDate}` : ""}`
        ) : (
          <>Loading...</>
        )}
      </BodyText>
    </div>
  );
};

const Divider: React.FC<{ color: string }> = ({ color }) => (
  <div style={{ height: "1px", backgroundColor: color }}></div>
);

const PriceFooter: React.FC<{
  totalPrice: number | null;
  HeadlineBodyText: React.FC<{ children: React.ReactNode; className?: string }>;
  BodyText: React.FC<{ children: React.ReactNode; className?: string }>;
  trialing: boolean;
  useBodyStyle?: boolean;
  className?: string;
}> = ({
  totalPrice,
  trialing,
  HeadlineBodyText,
  BodyText,
  useBodyStyle,
  className,
}) => {
  const Label = useBodyStyle ? BodyText : HeadlineBodyText;
  const value = trialing
    ? "$0"
    : totalPrice != null
      ? `$${Math.floor(totalPrice / 100)}`
      : "Loading...";
  return (
    <div className="flex flex-row justify-between">
      <Label className={className}>Due Today</Label>
      <Label className={className}>{value}</Label>
    </div>
  );
};

const PriceToggle: React.FC<{
  isMonthly: boolean;
  setIsMonthly: (isMonthly: boolean) => void;
  BodyText: React.FC<{ children: React.ReactNode; className?: string }>;
  theme: Pick<SummaryCardTheme, "activeButtonColor">;
}> = ({ isMonthly, setIsMonthly, BodyText, theme }) => {
  const { logEvent } = useStatsigClient();
  const billingCycleEvent: RegisteredStatsigEvent = {
    eventName: "billing_cycle_switched",
  };
  return (
    <div>
      <button
        onClick={() => {
          setIsMonthly(!isMonthly);
          logEvent(billingCycleEvent);
        }}
      >
        <BodyText className={theme.activeButtonColor}>
          {isMonthly ? "Switch to annual billing" : "Switch to monthly billing"}
        </BodyText>
      </button>
    </div>
  );
};

const SummaryCard: React.FC<SummaryCardProps> = ({
  totalPrice,
  monthlyPrice,
  isMonthly,
  setIsMonthly,
  clientTypography,
  theme,
  trialDays,
  productName,
}) => {
  // Extract typography components, with EscalatorText as fallback for EscalatorText
  const { HeadlineBodyText, BodyText, BodyTitleText, EscalatorText } =
    clientTypography;

  const isTrialActive = trialDays > 0;
  const startDate = new Date(
    Date.now() + trialDays * 24 * 60 * 60 * 1000
  ).toLocaleDateString();

  return (
    <div
      style={{
        padding: theme.padding ?? "24px 16px",
        margin: "16px 0 32px 0",
        borderRadius: theme.borderRadius ?? "12px",
      }}
      className={classNames(
        "flex flex-col gap-[12px] shadow-sm overflow-hidden",
        theme.backgroundColor
      )}
    >
      <EscalatorText
        className={classNames(
          "font-normal",
          theme.labelClassName ?? "text-[15px]",
          theme.labelColor ?? "text-black-100",
        )}
      >
        {theme.labelText ?? "ORDER SUMMARY"}
      </EscalatorText>
      <div>
        <PriceHeadline
          price={monthlyPrice}
          monthly={isMonthly}
          startDate={startDate}
          HeadlineBodyText={HeadlineBodyText}
          BodyText={BodyText}
          BodyTitleText={BodyTitleText}
          trialing={isTrialActive}
          trialDays={trialDays}
          productName={productName}
          trialColor={theme.trialColor}
          trialUseBodyStyle={theme.trialUseBodyStyle}
          trialCopyTemplate={theme.trialCopyTemplate}
          priceBodyClassName={theme.priceBodyClassName}
        />
        <PriceToggle
          isMonthly={isMonthly}
          setIsMonthly={setIsMonthly}
          BodyText={BodyText}
          theme={{ activeButtonColor: theme.activeButtonColor }}
        />
      </div>
      <Divider color={theme.dividerColor} />
      <PriceFooter
        totalPrice={totalPrice}
        HeadlineBodyText={HeadlineBodyText}
        BodyText={BodyText}
        trialing={isTrialActive}
        useBodyStyle={theme.footerUseBodyStyle}
        className={theme.footerClassName}
      />
    </div>
  );
};

export default SummaryCard;
